import { useGet } from "@albertine/api-client";
import type {
    FilterByCommitmentPeriod,
    FilterByMembershipTrial,
} from "@albertine/types";
import type {
    ListMembersFacetCount,
    ListMembersQuery,
} from "@albertine/types/openapi/lmt";
import { MemberMarkers } from "@prototypes/member-markers/MemberMarkers";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Lib } from "albertine-shared-web";
import { parseISO } from "date-fns";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { useAgentsMap } from "../../common/hooks/useAgents";
import { useAgentTeams } from "../../common/hooks/useAgentTeams";
import type { Agent } from "../../common/types/Agent";
import type { Dictionary } from "../../common/types/Dictionary";
import { EngagementLevelMap } from "../../common/types/Engagement";
import { MemberGroupTitles } from "../../common/types/FilterGroups";
import type { SortSettings } from "../../common/types/Sort";
import {
    countByTeamId,
    type FilterByAssignee,
    teamFilterItems,
    unassignedAgent,
} from "../../common/utils/agents.util";
import { showTimeComparison } from "../../common/utils/time.util";
import { useMembersUrlFilter, useUrlParams } from "../../utils/customHook.util";
import {
    type FilterByEngagementLevel,
    homeLocation,
    lastKnownPlace,
} from "../../utils/members.util";
import { memberNameBySurname } from "../../utils/name.util";
import { isMobile } from "../../utils/window.util";
import { AgentAvatar, MemberAvatar } from "../Common/Avatar";
import Button from "../Common/Buttons";
import { FormattedTime } from "../Common/DateTime";
import EngagementIndicator from "../Common/EngagementIndicator";
import { FlexInline } from "../Common/Flex";
import Icon from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import { List } from "../Common/List";
import ListFilter from "../Common/ListFilter";
import ListFilterGroup from "../Common/ListFilterGroup";
import MembershipTile from "../Common/membership/MembershipTile";
import SortItemsBy from "../Common/SortItemsBy";
import Tag from "../Common/Tag";
import Tooltip from "../Common/Tooltip";
import { InviteNewMember } from "../InviteNewMember/InviteNewMember";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import AssignMemberToAgent from "./AssignToAgent";
import {
    AgentEngagementLevelMap,
    type FilterByAgentEngagementLevel,
} from "./filters/AgentEngagementFilters";
import { type FilterByRequest, RequestFilterMap } from "./filters/RequestFilters";
import { MembersSearchFilter } from "./MembersSearchFilter";
import {
    type PagedMemberListItem as MemberListItem,
    usePagedMembers,
} from "./usePagedMembers";
import "./MembersList.css";

function composeMemberCell(member: MemberListItem): React.JSX.Element {
    const memberLastKnownPlace = lastKnownPlace(member);

    return (
        <>
            <div className="members-list__member">
                <MemberAvatar memberId={member.id} size="big" />
            </div>
            <Lib.Flex.Column gap="value4">
                <FlexInline gap="xsmall" alignSelf="start">
                    <Label.Medium.Light>
                        {memberNameBySurname(member)}
                    </Label.Medium.Light>
                    <MemberMarkers
                        isTestMember={member.isTestMember}
                        isVipMember={member.isVipMember}
                    />
                </FlexInline>
                <Lib.Flex.Row>
                    <Tooltip label="Home address" placement="bottom">
                        <FlexInline gap="xsmall">
                            <Icon.LocationOn.XSmall.N50 />
                            <Label.Small.Light.N50>
                                {homeLocation(member)}
                            </Label.Small.Light.N50>
                        </FlexInline>
                    </Tooltip>
                    <InlineTextDelimiter />
                    {member.lastSeenAt ? (
                        <Tooltip label="Member last seen" placement="bottom">
                            <FlexInline gap="xsmall">
                                <Icon.Sensors.XSmall.N50 />
                                <Label.Small.Light.N50>
                                    Last seen{" "}
                                    <FormattedTime.Comparison
                                        date={new Date(member.lastSeenAt)}
                                    />
                                    {memberLastKnownPlace
                                        ? ` in ${memberLastKnownPlace}`
                                        : null}
                                </Label.Small.Light.N50>
                            </FlexInline>
                        </Tooltip>
                    ) : null}
                </Lib.Flex.Row>
            </Lib.Flex.Column>
        </>
    );
}

function composeRequestCell(member: MemberListItem): React.JSX.Element {
    const totalConfirmedBookings = member.confirmedBookingsCount;

    return (
        <Lib.Flex.Column
            gap="value4"
            alignItems="start"
            className="members-list__requests"
        >
            <Tooltip label="Active / Total">
                <strong>{member.activeRequests}</strong>
                <span>&nbsp;/&nbsp;</span>
                {member.totalRequests}
            </Tooltip>
            <Tooltip
                label={
                    totalConfirmedBookings > 1
                        ? `${totalConfirmedBookings} confirmed bookings`
                        : `${totalConfirmedBookings} confirmed booking`
                }
                placement="bottom"
            >
                <Label.Small.Light.N50>{`${totalConfirmedBookings} booked`}</Label.Small.Light.N50>
            </Tooltip>
        </Lib.Flex.Column>
    );
}

function composeEngagementCell(member: MemberListItem): React.JSX.Element {
    const lastMessageByMemberAt = member.lastMessageByMemberAt
        ? parseISO(member.lastMessageByMemberAt)
        : null;
    const lastMessageByMemberInfo = lastMessageByMemberAt
        ? showTimeComparison(lastMessageByMemberAt, new Date())
        : null;

    return (
        <Lib.Flex.Column gap="value4" className="members-list__engagement">
            <EngagementIndicator lastMessageAt={lastMessageByMemberAt} />
            {lastMessageByMemberInfo && (
                <Tooltip label="Last message from member" placement="bottom">
                    <FlexInline gap="xsmall">
                        <Icon.MarkUnreadChatAlt.XSmall.N50 />
                        <Label.Small.Light.N50>
                            {lastMessageByMemberInfo}
                        </Label.Small.Light.N50>
                    </FlexInline>
                </Tooltip>
            )}
        </Lib.Flex.Column>
    );
}

function composeAssignedToCell(
    member: MemberListItem,
    agents: Dictionary<Agent>,
    currentAgent: Agent,
): React.JSX.Element {
    const assignee = member.assignedTo ? agents[member.assignedTo] : null;

    const lastMessageByAgentInfo = member.lastMessageByAgentAt
        ? showTimeComparison(parseISO(member.lastMessageByAgentAt), new Date())
        : null;
    const lastReplyAgent = member.lastMessageByAgentAgentId
        ? agents[member.lastMessageByAgentAgentId]
        : null;
    const lastMessageByAgentFirstName =
        lastReplyAgent?.id === assignee?.id ? null : lastReplyAgent?.firstName;

    return (
        <Lib.Flex.Row gap="value8" className="members-list__assignee">
            {!isMobile() && !assignee && currentAgent && (
                <AssignMemberToAgent
                    agentToAssign={currentAgent}
                    memberId={member.id}
                />
            )}
            {assignee && <AgentAvatar agentId={member.assignedTo} size="mid" />}
            <Lib.Flex.Column gap="value2">
                {assignee && (
                    <span className="members-list__assignee-name">{assignee.name}</span>
                )}
                {!isMobile() && assignee && lastMessageByAgentInfo && (
                    <Tooltip label="Last message from us" placement="bottom">
                        <FlexInline gap="xsmall">
                            <Icon.Reply.XSmall.N50 />
                            <Label.Small.Light.N50>
                                {lastMessageByAgentFirstName
                                    ? `${lastMessageByAgentInfo} by ${lastMessageByAgentFirstName}`
                                    : lastMessageByAgentInfo}
                            </Label.Small.Light.N50>
                        </FlexInline>
                    </Tooltip>
                )}
            </Lib.Flex.Column>
        </Lib.Flex.Row>
    );
}

type Props = {
    currentAgent: Agent;
};

function MembersList({ currentAgent }: Props) {
    const { urlState, setUrlState } = useUrlParams();
    const [search, setSearch] = useState<string>();
    const membersFilter = useMembersUrlFilter();
    const title = membersFilter ? MemberGroupTitles[membersFilter] : "Members";
    const initialSort: SortSettings = {
        by: membersFilter === "lapsed-and-churned" ? "membershipTrialExpiry" : "name",
        order: membersFilter === "lapsed-and-churned" ? "desc" : "asc",
    };

    const agentsMap = useAgentsMap();
    const teams = useAgentTeams();
    const { data: membershipTiers = [] } = useGet("membershipTiers", {});
    const memberQuery: Omit<ListMembersQuery, "continuationToken"> = {
        pageSize: 50,
        memberGroup: membersFilter ?? "default",
        sortBy: toListMembersSortField(urlState.sortBy ?? initialSort.by),
        sortOrder: urlState.sortOrder ?? initialSort.order,
        search,
        groups: urlState.group,
        engagement: urlState.engagement,
        assignedTo: urlState.assignedTo,
        teamIds: urlState.teamIds,
        requests: urlState.request,
        agentEngagement: urlState.agentEngagement,
        membershipTiers: urlState.membershipTier,
        membershipStages: urlState.membershipStage,
    };
    const {
        members,
        total,
        facets,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = usePagedMembers(memberQuery);
    const updateSearch = useCallback((value: string | undefined) => {
        setSearch(value);
    }, []);

    const membersScrollParentRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: members.length,
        getScrollElement: () => membersScrollParentRef.current,
        estimateSize: () => 96,
        overscan: 10,
    });
    const virtualRows = rowVirtualizer.getVirtualItems();
    const handleMembersScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

            if (distanceFromBottom <= 200 && hasNextPage && !isFetchingNextPage) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    );

    const assignees = (facets?.assignees ?? []).map((facet) => ({
        assignee:
            facet.id === unassignedAgent.id
                ? unassignedAgent
                : (agentsMap[facet.id] ?? {
                      id: facet.id,
                      name: facet.id,
                  }),
        count: facet.count,
    }));
    const teamCounts = countByTeamId(
        teams,
        assignees.map((item) => ({ id: item.assignee.id, count: item.count })),
    );
    const selectedTeamIds = urlState.teamIds ?? [];
    const memberEngagements = toNamedFacetCounts(
        facets?.engagement,
        EngagementLevelMap,
    );
    const agentEngagements = toNamedFacetCounts(
        facets?.agentEngagement,
        AgentEngagementLevelMap,
    );
    const requestCounts = toNamedFacetCounts(facets?.requests, RequestFilterMap);
    const membershipTierCountById = toFacetCountMap(facets?.membershipTiers);
    const membershipTierCounts = [
        {
            id: "no-tier",
            name: "Not set",
            active: true,
            default: false,
        },
        ...membershipTiers.filter((tier) => tier.active),
    ].map((tier) => ({
        ...tier,
        count: membershipTierCountById.get(tier.id) ?? 0,
    }));
    const membershipStageCounts = toNamedFacetCounts(facets?.membershipStages, {
        trial: "Trial",
        paid: "Paid",
    });

    const [showNewMemberModal, setShowNewMemberModal] = useState<boolean>(false);

    return (
        <List.Tab className="members-list">
            <List.TabHeader className="members-list__tab-header">
                <Lib.Flex.Row gap="value8">
                    <div className="members-list__title">
                        <Lib.Flex.Row gap="value8">
                            <h3 className="members-list__title">{title}</h3>
                            <Tag.Medium.N90>{total}</Tag.Medium.N90>
                        </Lib.Flex.Row>
                    </div>

                    <div className="member-list__header-actions">
                        <MembersSearchFilter
                            className="member-list__search"
                            total={total}
                            onChange={updateSearch}
                        />
                    </div>
                    {membersFilter === "default" && (
                        <div>
                            <Button
                                className="members-list__new-member"
                                onClick={() => {
                                    setShowNewMemberModal(true);
                                }}
                            >
                                New Member
                            </Button>
                            {showNewMemberModal && (
                                <InviteNewMember
                                    defaultAssignedTo={currentAgent.id}
                                    onClose={() => {
                                        setShowNewMemberModal(false);
                                    }}
                                />
                            )}
                        </div>
                    )}
                </Lib.Flex.Row>
            </List.TabHeader>

            <List.Header>
                <List.HeaderColumn className="members-list__member-header">
                    <SortItemsBy
                        entries={[
                            ["name", "Member name"],
                            ["lastSeenOnline", "Last seen online"],
                            ["creationDate", "Member created"],
                            ["membershipTrialExpiry", "Trial expiring"],
                            ["activeRequests", "Number of active requests"],
                            ["messageByMember", "Last message sent by member"],
                            ["messageByAgent", "Last message sent by us"],
                        ]}
                        initialSortBy={urlState.sortBy || initialSort.by}
                        initialSortOrder={urlState.sortOrder || initialSort.order}
                        onChange={(by, order) => {
                            setUrlState((previousUrlState) => ({
                                ...previousUrlState,
                                sortOrder: order,
                                sortBy: by,
                            }));
                        }}
                    />
                </List.HeaderColumn>

                <List.HeaderColumn className="members-list__membership-header">
                    <ListFilterGroup<FilterByCommitmentPeriod | FilterByMembershipTrial>
                        title="Membership"
                        filters={[
                            {
                                title: "Membership type",
                                items: membershipTierCounts.map((it) => ({
                                    data: it,
                                    selected: (urlState.membershipTier || []).includes(
                                        it.id,
                                    ),
                                    count: it.count,
                                })),
                                onChange: (items) => {
                                    const newSelectedIds = items.map((it) => it.id);
                                    setUrlState((previousUrlState) => ({
                                        ...previousUrlState,
                                        membershipTier: newSelectedIds,
                                    }));
                                },
                            },
                            {
                                title: "Membership stage",
                                items: membershipStageCounts.map((it) => ({
                                    data: it,
                                    selected: (urlState.membershipStage || []).includes(
                                        it.id,
                                    ),
                                    count: it.count,
                                })),
                                onChange: (items) => {
                                    const newSelectedIds = items.map((it) => it.id);
                                    setUrlState((previousUrlState) => ({
                                        ...previousUrlState,
                                        membershipStage: newSelectedIds,
                                    }));
                                },
                            },
                        ]}
                        position="bottom-left"
                    />
                </List.HeaderColumn>

                <List.HeaderColumn className="members-list__requests-header">
                    <ListFilter<FilterByRequest>
                        title="Requests"
                        items={requestCounts.map((it) => ({
                            data: it,
                            selected: (urlState.request || []).includes(it.id),
                            count: it.count,
                        }))}
                        onChange={(newRequestFilters) => {
                            setUrlState((previousUrlState) => ({
                                ...previousUrlState,
                                request: newRequestFilters.map((it) => it.id),
                            }));
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
                <List.HeaderColumn className="members-list__engagement-header">
                    <ListFilter<FilterByEngagementLevel>
                        title="Engagement"
                        items={memberEngagements.map((it) => ({
                            data: it,
                            selected: (urlState.engagement || []).includes(it.id),
                            count: it.count,
                        }))}
                        onChange={(newEngagementLevels) => {
                            setUrlState((previousUrlState) => ({
                                ...previousUrlState,
                                engagement: newEngagementLevels.map((it) => it.id),
                            }));
                        }}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
                <List.HeaderColumn className="members-list__assignee-header">
                    <ListFilterGroup<FilterByAssignee | FilterByAgentEngagementLevel>
                        title="Assigned to"
                        filters={[
                            {
                                title: "Last message sent by us",
                                items: agentEngagements.map((it) => ({
                                    data: it,
                                    selected: (urlState.agentEngagement || []).includes(
                                        it.id,
                                    ),
                                    count: it.count,
                                })),
                                onChange: (items) => {
                                    const newSelectedIds = items.map((it) => it.id);
                                    setUrlState((previousUrlState) => ({
                                        ...previousUrlState,
                                        agentEngagement: newSelectedIds,
                                    }));
                                },
                            },
                            ...(teams.length > 0
                                ? [
                                      {
                                          title: "Teams",
                                          items: teamFilterItems(
                                              teams,
                                              selectedTeamIds,
                                              (teamId) => teamCounts[teamId] ?? 0,
                                          ),
                                          onChange: (
                                              selected: Array<
                                                  | FilterByAssignee
                                                  | FilterByAgentEngagementLevel
                                              >,
                                          ) => {
                                              setUrlState((previousUrlState) => ({
                                                  ...previousUrlState,
                                                  teamIds: selected.map(
                                                      (item) => item.id,
                                                  ),
                                              }));
                                          },
                                      },
                                  ]
                                : []),
                            {
                                title: "Assigned to",
                                items: assignees.map((it) => ({
                                    data: it.assignee,
                                    selected: (urlState.assignedTo || []).includes(
                                        it.assignee.id,
                                    ),
                                    count: it.count,
                                })),
                                onChange: (newAgentList) => {
                                    const newAgentIds = newAgentList.map((it) => it.id);
                                    setUrlState((previousUrlState) => ({
                                        ...previousUrlState,
                                        assignedTo: newAgentIds,
                                    }));
                                },
                            },
                        ]}
                        position="bottom-right"
                    />
                </List.HeaderColumn>
            </List.Header>

            <div
                ref={membersScrollParentRef}
                onScroll={handleMembersScroll}
                className="members-list__results ph-no-capture ph-ignore-input"
            >
                {isLoading ? (
                    <div className="members-list__loading">
                        <LoadingSpinner />
                    </div>
                ) : (
                    <div
                        className="members-list__virtual-spacer"
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: "100%",
                            position: "relative",
                        }}
                    >
                        {virtualRows.map((virtualRow) => {
                            const member = members[virtualRow.index];
                            return (
                                <div
                                    key={virtualRow.key}
                                    data-index={virtualRow.index}
                                    ref={rowVirtualizer.measureElement}
                                    className="members-list__virtual-row"
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: `translateY(${virtualRow.start}px)`,
                                    }}
                                >
                                    <List.Item
                                        id={member.id}
                                        link={`/member/${member.id}`}
                                    >
                                        <List.ItemColumn className="members-list__member-column">
                                            {composeMemberCell(member)}
                                        </List.ItemColumn>

                                        <List.ItemColumn className="members-list__membership-column">
                                            <MembershipTile member={member} />
                                        </List.ItemColumn>

                                        <List.ItemColumn className="members-list__requests-column">
                                            {composeRequestCell(member)}
                                        </List.ItemColumn>
                                        <List.ItemColumn className="members-list__engagement-column">
                                            {composeEngagementCell(member)}
                                        </List.ItemColumn>
                                        <List.ItemColumn className="members-list__assignee-column">
                                            {composeAssignedToCell(
                                                member,
                                                agentsMap,
                                                currentAgent,
                                            )}
                                        </List.ItemColumn>
                                    </List.Item>
                                </div>
                            );
                        })}
                    </div>
                )}
                {isFetchingNextPage ? (
                    <div className="members-list__loading">
                        <LoadingSpinner />
                    </div>
                ) : null}
            </div>
        </List.Tab>
    );
}

function toListMembersSortField(
    sortBy: SortSettings["by"],
): ListMembersQuery["sortBy"] {
    switch (sortBy) {
        case "name":
        case "lastSeenOnline":
        case "creationDate":
        case "membershipTrialExpiry":
        case "activeRequests":
        case "messageByMember":
        case "messageByAgent":
            return sortBy;
        case "eventDate":
        case "deadline":
        case "ticketNumber":
        case "mentionCreatedAt":
        case "mentionDueDate":
            return "name";
        default: {
            const exhaustiveSort: never = sortBy;
            return exhaustiveSort;
        }
    }
}

function toNamedFacetCounts<Id extends string>(
    facets: ListMembersFacetCount[] | undefined,
    names: Record<Id, string>,
): Array<{ id: Id; name: string; count: number }> {
    return (facets ?? [])
        .filter((facet) => Object.hasOwn(names, facet.id))
        .map((facet) => ({
            id: facet.id as Id,
            name: names[facet.id as Id],
            count: facet.count,
        }));
}

function toFacetCountMap(
    facets: ListMembersFacetCount[] | undefined,
): Map<string, number> {
    return new Map((facets ?? []).map((facet) => [facet.id, facet.count]));
}

export default MembersList;
